// Stage 3 — carve the embedded graph into an SDF. Rooms are metaball
// clusters (a main disc + 1–3 satellites, smooth-unioned), halls are
// capsules along edges. Water is sdf < 0; everything else is wall. The same
// analytic field is evaluated on the CPU here (collision, tests, viz) and
// in walls.slang on the GPU (rendering) — keep smin and the primitive set
// in lockstep with the shader.

import { mulberry32, floatIn, intIn } from '../rng'
import { T } from '../tunables'
import { roomRadius } from './embed'
import type { DunGraph, DungeonLayout, Vec2, WallCapsule, WallDisc } from './types'

export function carveLayout(graph: DunGraph, pos: Vec2[]): DungeonLayout {
  const rng = mulberry32((graph.seed ^ 0x5bf03635) >>> 0)
  const discs: WallDisc[] = []
  const capsules: WallCapsule[] = []

  for (const node of graph.nodes) {
    const p = pos[node.id]
    const mainR = roomRadius(node.sizeHint) * floatIn(rng, 0.92, 1.08)
    discs.push({ x: p.x, y: p.y, r: mainR, node: node.id })
    // Satellites break the perfect-circle read — the room edge becomes a
    // lobed watercolor blob once smooth-union rounds the joins. Bigger
    // rooms get more lobes (a hub basin is a shoreline, not a circle).
    const maxSat = Math.min(5, 1 + Math.round(node.sizeHint * 1.2))
    for (let s = 0, n = intIn(rng, 1, Math.max(1, maxSat)); s < n; s++) {
      const ang = rng() * Math.PI * 2
      const dist = mainR * floatIn(rng, 0.35, 0.7)
      discs.push({
        x: p.x + Math.cos(ang) * dist,
        y: p.y + Math.sin(ang) * dist,
        r: mainR * floatIn(rng, 0.45, 0.7),
        node: node.id,
      })
    }
  }

  graph.edges.forEach((e, i) => {
    const a = pos[e.a]
    const b = pos[e.b]
    // Locks and valves carve narrower doorways; throats/shortcuts (B3) and
    // the region gradient ride widthMult. EVERY hall is floored at
    // minHallHalfWidth — gating is the door plug's job (B4), never the
    // width's: a maxed specimen (~0.65 radius) must fit through any hall
    // whose door it has opened, or the world soft-locks. A future
    // squeeze-pore mechanic is the sanctioned way below the floor.
    const raw =
      (e.kind === 'lock' || e.kind === 'valve'
        ? T.dungeon.hallHalfWidth * 0.6
        : T.dungeon.hallHalfWidth * floatIn(rng, 0.85, 1.2)) * (e.widthMult ?? 1)
    const width = Math.max(raw, T.dungeon.minHallHalfWidth)
    capsules.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, r: width, edge: i })
  })

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const d of discs) {
    minX = Math.min(minX, d.x - d.r)
    minY = Math.min(minY, d.y - d.r)
    maxX = Math.max(maxX, d.x + d.r)
    maxY = Math.max(maxY, d.y + d.r)
  }

  return { graph, pos, discs, capsules, bounds: { minX, minY, maxX, maxY } }
}

// ── Analytic SDF ────────────────────────────────────────────────────────────

/** Polynomial smooth-min — must match smin() in walls.slang. */
function smin(a: number, b: number, k: number): number {
  const h = Math.max(k - Math.abs(a - b), 0) / k
  return Math.min(a, b) - h * h * k * 0.25
}

/** Signed distance to water: < 0 swimmable, > 0 inside wall. Closed
 *  shortcut doors (B3) are walls: their plug discs are subtracted from the
 *  water, so collision, spawning, and flood-fill all treat a shut door as
 *  bank. Must stay in lockstep with waterSdf() in walls.slang. */
export function layoutSdf(layout: DungeonLayout, x: number, y: number): number {
  const k = T.dungeon.sminK
  let d = Infinity
  for (const disc of layout.discs) {
    const dd = Math.hypot(x - disc.x, y - disc.y) - disc.r
    d = d === Infinity ? dd : smin(d, dd, k)
  }
  for (const c of layout.capsules) {
    const bax = c.bx - c.ax
    const bay = c.by - c.ay
    const pax = x - c.ax
    const pay = y - c.ay
    const len2 = bax * bax + bay * bay
    const h = len2 > 0 ? Math.max(0, Math.min(1, (pax * bax + pay * bay) / len2)) : 0
    const dd = Math.hypot(pax - bax * h, pay - bay * h) - c.r
    d = smin(d, dd, k)
  }
  if (layout.doors) {
    for (const door of layout.doors) {
      if (door.open) continue
      d = Math.max(d, -(Math.hypot(x - door.x, y - door.y) - door.r))
    }
  }
  return d
}

/** Normalized SDF gradient (points from water toward wall). */
export function layoutSdfGrad(layout: DungeonLayout, x: number, y: number): Vec2 {
  const e = 0.01
  const gx = layoutSdf(layout, x + e, y) - layoutSdf(layout, x - e, y)
  const gy = layoutSdf(layout, x, y + e) - layoutSdf(layout, x, y - e)
  const len = Math.hypot(gx, gy)
  if (len < 1e-9) return { x: 1, y: 0 }
  return { x: gx / len, y: gy / len }
}
